import { useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BrandMark } from "@bw/shared/components";
import { useAuth } from "@bw/shared/hooks";
import { validateReturnUrl } from "@bw/shared/utils";
import Box from "@mui/material/Box";
import { visuallyHidden } from "../../../common/visuallyHidden";
import { AuthForm } from "../components/AuthForm";
import { useAuthRedirect } from "../hooks/useAuthRedirect";
import { consumeAccountDeletionNotice } from "../../../app/accountDeletionNotice";

interface LoginPageProps {
  defaultReturnUrl?: string;
}

/**
 * Focused sign-in route; all authentication remains in the shared auth spine.
 * One column on every width: the brand mark, a compact "Sign in" task heading
 * with one line of context, the shared form, then the account links.
 */
export function LoginPage({ defaultReturnUrl = "/" }: LoginPageProps = {}) {
  const { loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [accountDeleted] = useState(consumeAccountDeletionNotice);

  useAuthRedirect(defaultReturnUrl);

  const returnUrl = searchParams.get("returnUrl");
  const goingToApprovals =
    validateReturnUrl(returnUrl).split(/[?#]/)[0] === "/me/family/approvals";

  if (loading) {
    return (
      <Stack
        aria-busy="true"
        role="status"
        spacing={1.5}
        sx={{ alignItems: "center", py: 4 }}
      >
        <CircularProgress size={28} />
        <Typography color="text.secondary">Loading…</Typography>
      </Stack>
    );
  }

  const q = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : "";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        justifyContent: "center",
        alignItems: { xs: "flex-start", sm: "center" },
        px: 2.5,
        py: { xs: 5, sm: 6 },
      }}
    >
      <Stack spacing={2.5} sx={{ width: "100%", maxWidth: 400, minWidth: 0 }}>
        {/* Brand mark instead of a large illustration; the brand names the page. */}
        <Box sx={{ width: 48, height: 48 }}>
          <BrandMark aria-hidden="true" size="100%" />
        </Box>
        <Typography component="h1" sx={visuallyHidden}>
          Badminton Warriors
        </Typography>

        {accountDeleted ? (
          <Alert severity="success" role="status">
            Your account has been deleted. Signing you out…
          </Alert>
        ) : null}

        {goingToApprovals ? (
          <Typography color="text.secondary" variant="body2">
            Approving your child&apos;s account? Sign in below, or create a free
            account first — either way, you&apos;ll go straight to the approval.
          </Typography>
        ) : null}

        <AuthForm mode="signIn" intro="Book courts, find a game, and compete." />

        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ textAlign: "center" }}
        >
          New here?{" "}
          <Link component={RouterLink} to={`/register${q}`} underline="none">
            Create account
          </Link>
          {" · "}
          <Link
            component={RouterLink}
            to={`/forgot-password${q}`}
            underline="none"
          >
            Forgot password?
          </Link>
        </Typography>

        <Typography
          color="text.secondary"
          variant="caption"
          sx={{ textAlign: "center" }}
        >
          By continuing you agree to our{" "}
          <Link
            component={RouterLink}
            to="/terms"
            target="_blank"
            rel="noopener"
          >
            Terms
          </Link>{" "}
          and{" "}
          <Link
            component={RouterLink}
            to="/privacy"
            target="_blank"
            rel="noopener"
          >
            Privacy Policy
          </Link>
          .
        </Typography>
      </Stack>
    </Box>
  );
}
